import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Timer, Square, Loader2, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { useSpeech } from "../hooks/useSpeech";

const TARGET_MIN = 25;

export function FocusTimer({ concept, sourceTitle, taskId, onLogged }) {
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const cuesRef = useRef({});
  const { speak } = useSpeech();

  useEffect(() => {
    const half = Math.floor(TARGET_MIN * 30), last = (TARGET_MIN - 1) * 60, end = TARGET_MIN * 60;
    const cue = (k, msg) => { if (!cuesRef.current[k]) { cuesRef.current[k] = true; speak(msg, `cue-${k}`); } };
    if (running && seconds === half) cue("half", `Halfway through your ${TARGET_MIN}-minute focus on ${concept}. Keep going.`);
    if (running && seconds === last) cue("last", `One minute left on ${concept}. Start wrapping up the idea you're on.`);
    if (running && seconds === end) cue("end", `Twenty-five minutes on ${concept} — well done. Log the session and take a short break.`);
  }, [seconds, running]); // eslint-disable-line

  useEffect(() => {
    if (!running) { clearInterval(ref.current); return; }
    ref.current = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const stop = async () => {
    setRunning(false);
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (seconds < 30) { toast.message("Under 30 seconds — not logged"); setSeconds(0); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/plan/study-sessions", { concept, minutes, source_title: sourceTitle, task_id: taskId, mark_done: minutes >= 10 });
      toast.success(`${minutes} min logged on ${concept} · ${data.concept_total_minutes} min total`);
      onLogged?.(data, minutes >= 10);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log session"); }
    finally { setSaving(false); setSeconds(0); cuesRef.current = {}; }
  };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0"), ss = String(seconds % 60).padStart(2, "0");
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`focus-timer-${taskId || concept}`}>
      {(running || seconds > 0) && <span className="font-mono text-xs text-[var(--aria-primary)] tabular-nums" data-testid="focus-timer-clock">{mm}:{ss}</span>}
      {!running && seconds === 0 && (
        <button onClick={() => setRunning(true)} data-testid="focus-timer-start" title={`Start a ${TARGET_MIN}-minute focus session — ARIA cues you at halfway and the final minute; minutes are logged to this concept's evidence timeline`}
          className="text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-primary)] inline-flex items-center gap-1"><Timer size={11} /> Focus</button>
      )}
      {running && <button onClick={() => setRunning(false)} data-testid="focus-timer-pause" className="text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"><Pause size={12} /></button>}
      {!running && seconds > 0 && <button onClick={() => setRunning(true)} data-testid="focus-timer-resume" className="text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"><Play size={12} /></button>}
      {seconds > 0 && (
        <button onClick={stop} disabled={saving} data-testid="focus-timer-stop" className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Square size={11} />} Log
        </button>
      )}
    </span>
  );
}
